// @flow
import React, { useState } from 'react';
import injectSheet from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { gql, useQuery, useMutation } from '@apollo/client';
import { flashMessage as __flashMessage } from '~/app/actions';
import EditPageMeta from './EditPageMeta';
import PageSettings from './PageSettings';
import { getStudioEditorUrl } from '~/lib/studioUrl';

type Props = {
  classes: Object,
};

const CreatePage = (props: Props) => {
  const { classes: c } = props;
  const { userId } = useSelector(mapStateToProps);

  const dispatch = useDispatch();
  const flashMessage = (message, type) => dispatch(__flashMessage(message, type));
  const location = useLocation();

  const { id } = useParams();
  const { search } = useLocation();
  const preset = new URLSearchParams(search).get('preset');
  // Create links can choose the category: /admin/page/new/HeaderFlexContent?category=/us/events
  const initialCategoryPath = new URLSearchParams(search).get('category') || '';
  const isStudio = id === 'studio' || location.pathname.endsWith('/page/new/studio');

  // getData
  const { data, loading: isFetching } = useQuery(QUERY, {
    variables: { id },
    skip: isStudio
  })
  const template = data?.template;

  // addPage
  const [__onClickCreate, { loading: templateLoading }] = useMutation(ACTION);
  const onClickCreate = (page) => __onClickCreate({
    variables: { page }
  });
  const [__onClickCreateStudio, { loading: studioLoading }] = useMutation(ACTION_STUDIO);
  const onClickCreateStudio = (page) => __onClickCreateStudio({
    variables: { page }
  });
  const createLoading = isStudio ? studioLoading : templateLoading;

  const [createFinished, setCreateFinished] = useState(false);
  const [pageId, setPageId] = useState('');
  const [redirecting, setRedirecting] = useState(false);

  const handleCreateNewPage = async (title, path, slug, category) => {
    if (!title) {
      flashMessage('Please fill in title.', 'ERROR');
      return;
    }

    const pagePath = `${path}/${slug}`;
    const data = isStudio
      ? {
        creatorId: userId,
        categoryId: category,
        path: pagePath,
        title,
        is_root: path === ''
      }
      : {
        creatorId: userId,
        categoryId: category,
        templateId: template.id,
        templateVersion: template.latestVersion,
        templateSettings: {
        ...(template.templateSettings || {}),
        ...(preset ? { presetType: preset } : {})
      },
        path: pagePath,
        title,
        is_root: path === ''
      };

    try {
      if (isStudio) {
        const obj = await onClickCreateStudio(data);
        const newId = obj?.data?.createStudioPageFromGenesis?._id;
        if (!newId) {
          throw new Error('Studio page creation failed');
        }
        setRedirecting(true);
        flashMessage('Studio page created. Opening Studio editor...');
        window.location.assign(getStudioEditorUrl(newId));
        return;
      }

      const obj = await onClickCreate(data);
      setPageId(obj.data.addPage._id);
      setCreateFinished(true);
      flashMessage('Successfully created the page!');
    } catch(e) {
      flashMessage(e?.message || 'This path already exist!', 'ERROR');
    }
  };

  if (isFetching) return <span>Loading...</span>;

  return (
    <div className={c.container}>
      <div className={c.header}>
        <div className={c.title}>Create Page</div>
      </div>
      <div className={c.body}>
        <PageSettings
          disabled={createFinished || redirecting}
          copyLoading={createLoading || redirecting}
          loadingLabel={redirecting ? 'Opening editor…' : 'Loading...'}
          templateId={template?.id}
          templateName={isStudio ? 'Studio (no template)' : undefined}
          editorType={isStudio ? 'studio' : 'genesis'}
          initialCategoryPath={initialCategoryPath}
          onCreate={handleCreateNewPage}
        />
      </div>
      {createFinished && !isStudio && (
        <EditPageMeta pageId={pageId} />
      )}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: '20px 30px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  }
};

/* =============================================================================
=    GraphQL: Get categories/template
============================================================================= */
const QUERY = gql`
  query getData($id: ID!) {
    template(id: $id) {
      id
      versions
      latestVersion
    }
  }
`;

/* =============================================================================
=    GraphQL: Add New Page
============================================================================= */
const ACTION = gql`
  mutation addPage($page: CreatePageInput) {
    addPage(page: $page) {
      _id
    }
  }
`;

const ACTION_STUDIO = gql`
  mutation createStudioPageFromGenesis($page: CreateStudioPageFromGenesisInput) {
    createStudioPageFromGenesis(page: $page) {
      _id
    }
  }
`;

/* =============================================================================
=    Redux
============================================================================= */
const mapStateToProps = state => ({
  userId: state.app.user?._id
});

export default injectSheet(styles)(CreatePage);
